/* Vanessa Mena World — orquestador de animaciones y shaders (secciones vm-*)
 *
 * Dependencias (pineadas, self-hosted en assets/):
 *  - Motion 12.42.2 -> window.Motion (vm-vendor-motion.js, defer, cargado antes en theme.liquid)
 *  - @paper-design/shaders 0.0.77 -> import() dinamico via window.VM_ASSETS.shaders
 *
 * Contrato de data-attributes para las secciones:
 *  [data-vm-glow]   borde de luz SVG sin WebGL (reemplaza a pulsing en CTAs y card de escasez)
 *  [data-vm-shader="grain|mesh|dither|pulsing"]  host del canvas WebGL (position:relative, con tamano)
 *    comunes:  data-vm-colors="#hex,#hex,..."  data-vm-speed="0.5"
 *              data-vm-scale  data-vm-rotation (grados)  data-vm-offset-x  data-vm-offset-y
 *              (scale/rotation/offset NO aplican a pulsing)
 *    grain:    data-vm-color-back  data-vm-shape="wave|dots|truchet|corners|ripple|blob|sphere"
 *              data-vm-softness  data-vm-intensity  data-vm-noise
 *    mesh:     data-vm-distortion  data-vm-swirl
 *    dither:   data-vm-image="url"  data-vm-color-back  data-vm-color-front  data-vm-color-highlight
 *              data-vm-px-size  data-vm-color-steps  data-vm-dither-type="random|2x2|4x4|8x8"
 *              data-vm-original-colors
 *    pulsing:  borde de luz animado (PulsingBorder); el canvas debe medir exactamente el elemento
 *              data-vm-color-back (default transparente)  data-vm-roundness (1 = pill)
 *              data-vm-thickness  data-vm-softness  data-vm-intensity  data-vm-bloom
 *              data-vm-spots  data-vm-spot-size  data-vm-pulse  data-vm-smoke  data-vm-smoke-size
 *  [data-vm-reveal]                      reveal on-scroll (opacity + translateY)
 *    data-vm-reveal-delay="0.15"         retraso en segundos
 *  [data-vm-scrollfill]                  seccion Scroll Fill (wrapper alto con hijo sticky)
 *    .vm-sf-media                        contenedor imagen+dither (entra primero)
 *    .vm-sf-text                         bloque de texto (entra despues)
 *    .vm-fill-line                       lineas que se rellenan segun scroll (background-clip: text)
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const MOBILE = window.matchMedia('(max-width: 767px)').matches;

const GRAIN_SHAPES = { wave: 1, dots: 2, truchet: 3, corners: 4, ripple: 5, blob: 6, sphere: 7 };
const DITHER_TYPES = { random: 1, '2x2': 2, '4x4': 3, '8x8': 4 };

let shadersModulePromise = null;
const cleanupRegistry = new Map();
const hostMounts = new WeakMap();

function loadShaders() {
  if (!shadersModulePromise) {
    shadersModulePromise = import(window.VM_ASSETS.shaders);
  }
  return shadersModulePromise;
}

function motion() {
  return window.Motion || null;
}

function registerCleanup(rootSection, fn) {
  if (!cleanupRegistry.has(rootSection)) cleanupRegistry.set(rootSection, []);
  cleanupRegistry.get(rootSection).push(fn);
}

function sectionRootOf(el) {
  return el.closest('.shopify-section') || document.body;
}

function parseColors(value) {
  return (value || '').split(',').map((c) => c.trim()).filter(Boolean);
}

function sizingUniforms(host) {
  const d = host ? host.dataset : {};
  return {
    u_fit: 2,
    u_scale: parseFloat(d.vmScale || '1'),
    u_rotation: parseFloat(d.vmRotation || '0'),
    u_offsetX: parseFloat(d.vmOffsetX || '0'),
    u_offsetY: parseFloat(d.vmOffsetY || '0'),
    u_originX: 0.5,
    u_originY: 0.5,
    u_worldWidth: 0,
    u_worldHeight: 0,
  };
}

/* ------------------------------------------------------------------ */
/* Shaders                                                             */
/* ------------------------------------------------------------------ */

async function mountShader(host) {
  const type = host.dataset.vmShader;
  const speed = REDUCED ? 0 : parseFloat(host.dataset.vmSpeed || '0.5');
  const mod = await loadShaders();
  const { ShaderMount, getShaderColorFromString } = mod;
  const color = (v, fallback) => getShaderColorFromString(v || fallback);

  let fragment = null;
  let uniforms = null;
  let mipmaps;

  if (type === 'grain') {
    const colors = parseColors(host.dataset.vmColors);
    fragment = mod.grainGradientFragmentShader;
    // La textura de ruido carga async; ShaderMount exige que este completa
    const noiseTexture = mod.getShaderNoiseTexture();
    if (noiseTexture && !(noiseTexture.complete && noiseTexture.naturalWidth)) {
      await new Promise((resolve) => {
        noiseTexture.addEventListener('load', resolve, { once: true });
        noiseTexture.addEventListener('error', resolve, { once: true });
      });
    }
    uniforms = {
      ...sizingUniforms(host),
      u_colorBack: color(host.dataset.vmColorBack, '#0C0A08'),
      u_colors: colors.map((c) => color(c)),
      u_colorsCount: colors.length,
      u_softness: parseFloat(host.dataset.vmSoftness || '0.6'),
      u_intensity: parseFloat(host.dataset.vmIntensity || '0.45'),
      u_noise: parseFloat(host.dataset.vmNoise || '0.35'),
      u_shape: GRAIN_SHAPES[host.dataset.vmShape || 'wave'] || GRAIN_SHAPES.wave,
      u_noiseTexture: noiseTexture,
    };
  } else if (type === 'mesh') {
    const colors = parseColors(host.dataset.vmColors);
    fragment = mod.meshGradientFragmentShader;
    uniforms = {
      ...sizingUniforms(host),
      u_colors: colors.map((c) => color(c)),
      u_colorsCount: colors.length,
      u_distortion: parseFloat(host.dataset.vmDistortion || '0.8'),
      u_swirl: parseFloat(host.dataset.vmSwirl || '0.2'),
      u_grainMixer: 0,
      u_grainOverlay: 0,
    };
  } else if (type === 'dither') {
    const src = host.dataset.vmImage;
    if (!src) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve;
      img.src = src;
    });
    if (!img.complete || !img.naturalWidth) return;
    fragment = mod.imageDitheringFragmentShader;
    uniforms = {
      ...sizingUniforms(host),
      u_image: img,
      u_colorBack: color(host.dataset.vmColorBack, '#0C0A08'),
      u_colorFront: color(host.dataset.vmColorFront, '#9A927F'),
      u_colorHighlight: color(host.dataset.vmColorHighlight, '#F4EFE3'),
      u_type: DITHER_TYPES[host.dataset.vmDitherType || '4x4'] || DITHER_TYPES['4x4'],
      u_pxSize: parseFloat(host.dataset.vmPxSize || '2'),
      u_colorSteps: parseInt(host.dataset.vmColorSteps || '2', 10),
      u_originalColors: host.dataset.vmOriginalColors === 'true',
      u_inverted: false,
    };
    mipmaps = ['u_image'];
  } else if (type === 'pulsing') {
    const colors = parseColors(host.dataset.vmColors);
    fragment = mod.pulsingBorderFragmentShader;
    const noiseTexture = mod.getShaderNoiseTexture();
    if (noiseTexture && !(noiseTexture.complete && noiseTexture.naturalWidth)) {
      await new Promise((resolve) => {
        noiseTexture.addEventListener('load', resolve, { once: true });
        noiseTexture.addEventListener('error', resolve, { once: true });
      });
    }
    // El vertex shader exige los sizing uniforms; pulsing usa fit "none" (0)
    // para que el borde abrace exactamente el canvas del host
    uniforms = {
      ...sizingUniforms(host),
      u_fit: 0,
      u_noiseTexture: noiseTexture,
      u_colorBack: color(host.dataset.vmColorBack, '#00000000'),
      u_colors: colors.map((c) => color(c)),
      u_colorsCount: colors.length,
      u_roundness: parseFloat(host.dataset.vmRoundness || '1'),
      u_thickness: parseFloat(host.dataset.vmThickness || '0.015'),
      u_softness: parseFloat(host.dataset.vmSoftness || '0.7'),
      u_intensity: parseFloat(host.dataset.vmIntensity || '0.5'),
      u_bloom: parseFloat(host.dataset.vmBloom || '0.4'),
      u_spots: parseFloat(host.dataset.vmSpots || '3'),
      u_spotSize: parseFloat(host.dataset.vmSpotSize || '0.35'),
      u_pulse: parseFloat(host.dataset.vmPulse || '0'),
      u_smoke: parseFloat(host.dataset.vmSmoke || '0.15'),
      u_smokeSize: parseFloat(host.dataset.vmSmokeSize || '0.5'),
      u_marginTop: 0,
      u_marginRight: 0,
      u_marginBottom: 0,
      u_marginLeft: 0,
      u_aspectRatio: 0,
    };
  } else {
    return;
  }

  const minPixelRatio = MOBILE ? 1 : 2;
  const maxPixelCount = MOBILE ? 1920 * 1080 : 1920 * 1080 * 4;
  // Se monta pausado (costo cero); inView lo arranca al entrar al viewport.
  // pulsing arranca en un frame avanzado: en frame 0 los spots del borde casi no se ven
  const initialFrame = type === 'pulsing' ? 30000 : 0;
  const mount = new ShaderMount(host, fragment, uniforms, undefined, 0, initialFrame, minPixelRatio, maxPixelCount, mipmaps);

  const root = sectionRootOf(host);
  const disposers = [];
  hostMounts.set(host, disposers);
  const addDisposer = (fn) => {
    disposers.push(fn);
    registerCleanup(root, fn);
  };
  addDisposer(() => mount.dispose());

  const M = motion();
  if (M && speed > 0) {
    const stopObserver = M.inView(host, () => {
      mount.setSpeed(speed);
      return () => mount.setSpeed(0);
    }, { margin: '15% 0px 15% 0px' });
    addDisposer(stopObserver);
  } else if (speed > 0) {
    mount.setSpeed(speed);
  }

  // Movil (sobre todo Android) mata contextos WebGL bajo presion de memoria;
  // sin esto el canvas queda muerto (placeholder blanco de crash de Chrome)
  const canvas = host.querySelector('canvas');
  if (canvas) {
    const onLost = () => setTimeout(() => remountShader(host), 150);
    canvas.addEventListener('webglcontextlost', onLost, { once: true });
    addDisposer(() => canvas.removeEventListener('webglcontextlost', onLost));
  }
}

function remountShader(host) {
  const fns = hostMounts.get(host);
  if (fns) {
    hostMounts.delete(host);
    fns.forEach((fn) => {
      try { fn(); } catch (e) { /* dispose defensivo */ }
    });
  }
  host.querySelectorAll(':scope > canvas').forEach((c) => c.remove());
  mountShader(host);
}

/* ------------------------------------------------------------------ */
/* Glow border SVG (sin WebGL)                                         */
/* ------------------------------------------------------------------ */
/*
 *  [data-vm-glow]  host absoluto (inset:0) dentro de un contenedor con
 *                  overflow:hidden. Reemplaza al PulsingBorder de Paper:
 *                  2 spots recorren el perimetro (SMIL animateMotion) en
 *                  lados opuestos + bloom interior con feGaussianBlur.
 *    data-vm-radius   radio de esquina en px (default 12; se acota a h/2)
 *    data-vm-colors   colores de los spots separados por coma (default champagne,oro)
 *    data-vm-duration segundos por vuelta (default 9)
 *  Sin JS queda el borde estatico del host (fallback).
 */
let glowUid = 0;

function buildGlowSvg(host) {
  const w = host.offsetWidth;
  const h = host.offsetHeight;
  if (!w || !h) return null;
  const id = 'vmg' + (glowUid++);
  const colors = parseColors(host.dataset.vmColors || '#E7CD96,#C9992E');
  const dur = parseFloat(host.dataset.vmDuration || '9');
  const r = Math.min(parseFloat(host.dataset.vmRadius || '12'), h / 2);
  // rounded-rect inset 1px: el borde se dibuja sobre la orilla del host
  const i = 1, x = i, y = i, W = w - 2 * i, H = h - 2 * i, rr = Math.max(0, r - i);
  const d = 'M' + (x + rr) + ',' + y +
    ' H' + (x + W - rr) + ' A' + rr + ',' + rr + ' 0 0 1 ' + (x + W) + ',' + (y + rr) +
    ' V' + (y + H - rr) + ' A' + rr + ',' + rr + ' 0 0 1 ' + (x + W - rr) + ',' + (y + H) +
    ' H' + (x + rr) + ' A' + rr + ',' + rr + ' 0 0 1 ' + x + ',' + (y + H - rr) +
    ' V' + (y + rr) + ' A' + rr + ',' + rr + ' 0 0 1 ' + (x + rr) + ',' + y + ' Z';
  const spotR = Math.min(90, Math.max(28, Math.min(w, h) * 0.55));
  const n = colors.length;
  // misma duracion, desfase uniforme: los spots nunca se alcanzan
  const spots = colors.map((c, k) =>
    '<circle r="' + spotR + '" fill="url(#' + id + '-rg' + k + ')">' +
      '<animateMotion dur="' + dur + 's" begin="' + (-(dur / n) * k) + 's" repeatCount="indefinite" rotate="auto">' +
        '<mpath href="#' + id + '-p"/>' +
      '</animateMotion></circle>'
  ).join('');
  const grads = colors.map((c, k) =>
    '<radialGradient id="' + id + '-rg' + k + '">' +
      '<stop offset="0" stop-color="' + c + '"/>' +
      '<stop offset=".45" stop-color="' + c + '" stop-opacity=".55"/>' +
      '<stop offset="1" stop-color="' + c + '" stop-opacity="0"/>' +
    '</radialGradient>'
  ).join('');
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.setAttribute('aria-hidden', 'true');
  svg.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;pointer-events:none;';
  svg.innerHTML =
    '<defs>' +
      '<path id="' + id + '-p" d="' + d + '"/>' + grads +
      '<clipPath id="' + id + '-clip"><path d="' + d + '"/></clipPath>' +
      '<mask id="' + id + '-ring" maskUnits="userSpaceOnUse"><path d="' + d + '" fill="none" stroke="#fff" stroke-width="1.6"/></mask>' +
      '<mask id="' + id + '-ringsoft" maskUnits="userSpaceOnUse"><path d="' + d + '" fill="none" stroke="#fff" stroke-width="5" opacity=".9" filter="url(#' + id + '-b1)"/></mask>' +
      '<filter id="' + id + '-b1" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="2.5"/></filter>' +
      '<filter id="' + id + '-b2" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="' + (spotR * 0.35) + '"/></filter>' +
    '</defs>' +
    '<path d="' + d + '" fill="none" stroke="rgba(231,205,150,.32)" stroke-width="1"/>' +
    '<g clip-path="url(#' + id + '-clip)" opacity=".3" filter="url(#' + id + '-b2)">' + spots + '</g>' +
    '<g mask="url(#' + id + '-ringsoft)" opacity=".7">' + spots + '</g>' +
    '<g mask="url(#' + id + '-ring)">' + spots + '</g>';
  return svg;
}

function initGlow(host) {
  let current = null;
  let lastKey = '';
  const render = () => {
    const key = host.offsetWidth + 'x' + host.offsetHeight;
    if (key === lastKey) return;
    lastKey = key;
    if (current) current.remove();
    current = buildGlowSvg(host);
    if (current) {
      host.appendChild(current);
      host.classList.add('has-glow');
    }
  };
  render();
  let raf = 0;
  const ro = new ResizeObserver(() => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(render);
  });
  ro.observe(host);
  registerCleanup(sectionRootOf(host), () => {
    ro.disconnect();
    if (current) current.remove();
  });
}

/* ------------------------------------------------------------------ */
/* Reveals genericos                                                   */
/* ------------------------------------------------------------------ */

function initReveal(el) {
  const M = motion();
  if (!M || REDUCED) return;
  el.style.opacity = '0';
  el.style.transform = 'translateY(24px)';
  const delay = parseFloat(el.dataset.vmRevealDelay || '0');
  const stop = M.inView(el, () => {
    M.animate(
      el,
      { opacity: [0, 1], transform: ['translateY(24px)', 'translateY(0px)'] },
      { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }
    );
  }, { margin: '0px 0px -12% 0px', amount: 0.2 });
  registerCleanup(sectionRootOf(el), stop);
}

/* ------------------------------------------------------------------ */
/* Scroll Fill                                                         */
/* ------------------------------------------------------------------ */

function initScrollFill(wrapper) {
  const M = motion();
  const media = wrapper.querySelector('.vm-sf-media');
  const text = wrapper.querySelector('.vm-sf-text');
  const lines = Array.from(wrapper.querySelectorAll('.vm-fill-line'));
  const root = sectionRootOf(wrapper);

  if (REDUCED || !M) {
    lines.forEach((l) => l.classList.add('is-filled'));
    return;
  }

  // Entrada en 2 tiempos: primero imagen+dither, despues el texto
  if (media) {
    media.style.opacity = '0';
  }
  if (text) {
    text.style.opacity = '0';
  }
  const stopEntry = M.inView(wrapper, () => {
    if (media) {
      M.animate(media, { opacity: [0, 1], transform: ['scale(1.045)', 'scale(1)'] }, { duration: 1.4, ease: [0.22, 1, 0.36, 1] });
    }
    if (text) {
      M.animate(text, { opacity: [0, 1], transform: ['translateY(26px)', 'translateY(0px)'] }, { duration: 1, delay: 1.05, ease: [0.22, 1, 0.36, 1] });
    }
  }, { amount: 0.15 });
  registerCleanup(root, stopEntry);

  // Relleno del texto segun progreso de scroll del wrapper (sticky)
  if (lines.length) {
    const n = lines.length;
    const cancelScroll = M.scroll(
      (progress) => {
        for (let i = 0; i < n; i++) {
          const local = Math.min(1, Math.max(0, progress * n - i));
          lines[i].style.backgroundSize = (local * 100).toFixed(1) + '% 100%';
        }
      },
      { target: wrapper, offset: ['start start', 'end end'] }
    );
    registerCleanup(root, cancelScroll);
  }
}

/* ------------------------------------------------------------------ */
/* Init / cleanup                                                      */
/* ------------------------------------------------------------------ */

function initScope(scope) {
  scope.querySelectorAll('[data-vm-shader]').forEach((el) => {
    if (el.dataset.vmInit) return;
    el.dataset.vmInit = '1';
    mountShader(el);
  });
  scope.querySelectorAll('[data-vm-glow]').forEach((el) => {
    if (el.dataset.vmInit) return;
    el.dataset.vmInit = '1';
    initGlow(el);
  });
  scope.querySelectorAll('[data-vm-reveal]').forEach((el) => {
    if (el.dataset.vmInit) return;
    el.dataset.vmInit = '1';
    initReveal(el);
  });
  scope.querySelectorAll('[data-vm-scrollfill]').forEach((el) => {
    if (el.dataset.vmInit) return;
    el.dataset.vmInit = '1';
    initScrollFill(el);
  });
}

function cleanupScope(rootSection) {
  const fns = cleanupRegistry.get(rootSection);
  if (!fns) return;
  fns.forEach((fn) => {
    try { fn(); } catch (e) { /* dispose defensivo */ }
  });
  cleanupRegistry.delete(rootSection);
}

initScope(document);

// bfcache: al volver a la pagina (atras/adelante o reabrir pestana en movil)
// el DOM se restaura tal cual pero los contextos WebGL vuelven muertos
window.addEventListener('pageshow', (e) => {
  if (!e.persisted) return;
  document.querySelectorAll('[data-vm-shader]').forEach((host) => {
    if (host.dataset.vmInit) remountShader(host);
  });
});

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => initScope(e.target));
  document.addEventListener('shopify:section:unload', (e) => cleanupScope(e.target));
}
